import React, { useEffect, useState } from 'react';
import { Sparkles, Sofa, BedDouble, UtensilsCrossed, Trees, X } from 'lucide-react';
import MatchedProjectBundle from './MatchedProjectBundle';
import { ROOM_BUNDLES, ROOM_ORDER } from '../../data/makeItHomeBundles';

const ROOM_ICON = { living: Sofa, bedroom: BedDouble, dining: UtensilsCrossed, outdoor: Trees };
const SEEN_KEY = 'bp:makeItHomeSeen';

/**
 * MakeItHomeBundles — the completion / "now make it home" module. Tabs across
 * rooms (Living / Bedroom / Dining / Outdoors); each tab renders a swappable
 * MatchedProjectBundle so the set feels personalised, with a running total.
 *
 * Reuses the exact bundle engine the bathroom/kitchen reno bundles use. Kept
 * in the "products we've matched you with" voice (not a store). Anonymous-safe
 * (first-visit celebratory ribbon remembered in localStorage only).
 *
 * Props let the Love Living hub and the workspace share it:
 *  - eyebrow / title / intro: heading copy
 *  - rooms: ordered room keys to show (defaults to all four)
 *  - initialRoom: which tab opens first
 *  - celebrate: show the one-time "welcome home" ribbon
 */
const MakeItHomeBundles = ({
  eyebrow = "Now, let's make it home",
  title = "The hard part's done. Now the fun part.",
  intro = "We helped you get the home built — now let us help you live in it. Here are matched sets people reach for once they move in, gathered across brands. Swap any piece; you buy each one direct from the retailer at their price.",
  rooms = ROOM_ORDER,
  initialRoom,
  celebrate: celebrateProp = false,
  testid = 'make-it-home',
}) => {
  const order = rooms.filter((r) => ROOM_BUNDLES[r]);
  const [active, setActive] = useState(order.includes(initialRoom) ? initialRoom : order[0]);
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    if (!celebrateProp) return;
    try { setCelebrate(!window.localStorage.getItem(SEEN_KEY)); } catch { /* ignore */ }
  }, [celebrateProp]);

  const dismissCelebrate = () => {
    setCelebrate(false);
    try { window.localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ }
  };

  if (!order.length) return null;
  const room = ROOM_BUNDLES[active];

  return (
    <section className="py-12 sm:py-16" data-testid={testid}>
      <div className="max-w-[1100px] mx-auto px-5 sm:px-6">
        {celebrate && (
          <div
            className="mb-6 flex items-start justify-between gap-4 rounded-2xl border border-[#1d8efe]/20 bg-[#f0f6ff] px-5 py-4"
            data-testid={`${testid}-ribbon`}
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1d8efe] text-white">
                <Sparkles className="h-4 w-4" strokeWidth={2.25} />
              </span>
              <p className="text-[14px] leading-[1.55] text-[#1d1d1f]">
                <span className="font-semibold">Welcome home.</span> You've gone from plans to keys — we're proud to have been in your corner. Here's a little help with the fun part.
              </p>
            </div>
            <button
              type="button"
              onClick={dismissCelebrate}
              aria-label="Dismiss"
              className="shrink-0 rounded-full p-1.5 text-[#86868b] transition-colors hover:bg-white hover:text-[#1d1d1f]"
              data-testid={`${testid}-ribbon-dismiss`}
            >
              <X className="h-4 w-4" strokeWidth={2.25} />
            </button>
          </div>
        )}

        <div className="mb-5">
          <div className="text-[12px] font-semibold text-[#1d8efe] mb-2">{eyebrow}</div>
          <h2 className="text-[26px] sm:text-[32px] font-bold text-[#1d1d1f] tracking-tight leading-[1.14]">{title}</h2>
          <p className="text-[15px] text-[#6b7078] mt-2.5 max-w-[680px] leading-[1.55]">{intro}</p>
        </div>

        {/* Room tabs */}
        <div className="flex flex-wrap gap-2" data-testid={`${testid}-tabs`}>
          {order.map((key) => {
            const Icon = ROOM_ICON[key];
            const on = key === active;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActive(key)}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13.5px] font-semibold border transition-colors ${on ? 'bg-[#1d1d1f] text-white border-[#1d1d1f]' : 'bg-white text-[#1d1d1f] border-gray-200 hover:border-[#1d8efe] hover:text-[#1d8efe]'}`}
                data-testid={`${testid}-tab-${key}`}
                aria-pressed={on}
              >
                {Icon && <Icon className="w-4 h-4" />} {ROOM_BUNDLES[key].label}
              </button>
            );
          })}
        </div>
      </div>

      {/* The swappable matched set for the active room. Keyed so switching
          tabs remounts and refetches cleanly. MatchedProjectBundle brings its
          own padded section + running total + per-slot Swap. */}
      <MatchedProjectBundle
        key={active}
        testid={`${testid}-bundle`}
        Icon={ROOM_ICON[active]}
        eyebrow=""
        title={room.title}
        intro={room.intro}
        seeAll={room.seeAll}
        needs={room.needs}
      />
    </section>
  );
};

export default MakeItHomeBundles;
